/**
 * MobileNavDrawer — the single shared off-canvas navigation drawer for every
 * portal's compact (mobile) layout (PMS dashboard + admin shell today).
 *
 * Lives in `entities/` (presentational, reused in 2+ places per Hybrid Z /
 * ADR frontend-0001) so the two portals share one drawer instead of drifting
 * copies. Each portal passes its own nav `items` (route-based) + `activeId` +
 * the signed-in `user`; the chrome (overlay, panel, header, footer, Escape +
 * backdrop close) is identical everywhere.
 *
 * Width min(320px, 86vw). Backdrop click + Escape close. Enabled items render
 * as `<Link>` (route navigation, closing the drawer on click); `disabled`
 * items (e.g. PMS phases not yet shipped) render as non-interactive rows.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, BrandMark, Icon, IconBtn, type IconName } from '@sparta/ui-kit';

export interface MobileNavItem {
  readonly id: string;
  readonly label: string;
  readonly icon: IconName;
  /** Route to navigate to (ignored when `disabled`). */
  readonly href: string;
  /** Not-yet-available destination — rendered as a non-interactive row. */
  readonly disabled?: boolean;
}

export interface MobileNavDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly items: readonly MobileNavItem[];
  readonly activeId: string;
  readonly user: { id: string; email: string; name?: string };
}

export function MobileNavDrawer({
  open,
  onClose,
  items,
  activeId,
  user,
}: MobileNavDrawerProps): React.ReactElement | null {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const displayName =
    user.name !== undefined && user.name.trim().length > 0 ? user.name : '—';

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 z-[49] bg-[var(--bg-overlay)]"
      />
      {/* Panel — `[box-shadow:var(--shadow-xl)]` arbitrary-property form is
          permitted per the ADR-0003 allow-list. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="fixed left-0 top-0 bottom-0 z-50 w-[min(320px,86vw)] bg-[var(--bg-surface)] p-5 flex flex-col gap-4 [box-shadow:var(--shadow-xl)] overflow-y-auto"
      >
        {/* Header: brand + close */}
        <div className="flex items-center gap-3">
          <BrandMark size={28} />
          <span className="font-bold text-lg tracking-[-0.2px]">Desata</span>
          <div className="flex-1" />
          <IconBtn label="Close menu" onClick={onClose}>
            <Icon name="close" size={20} />
          </IconBtn>
        </div>

        {/* Nav list */}
        <div className="flex flex-col gap-0.5">
          {items.map((item) => {
            const isActive = item.id === activeId;
            if (item.disabled) {
              return (
                <div
                  key={item.id}
                  aria-disabled="true"
                  className="flex items-center gap-3 py-3 px-3.5 min-h-11 rounded-xl text-[15px] text-[var(--fg-disabled)] cursor-not-allowed"
                >
                  <Icon name={item.icon} size={20} aria-hidden />
                  <span className="flex-1">{item.label}</span>
                </div>
              );
            }
            return (
              <Link
                key={item.id}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                onClick={onClose}
                className={`flex items-center gap-3 py-3 px-3.5 min-h-11 rounded-xl no-underline text-[15px] font-medium ${
                  isActive
                    ? 'bg-[var(--bg-brand)] text-[var(--fg-on-brand)] font-semibold'
                    : 'bg-transparent text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <Icon name={item.icon} size={20} aria-hidden />
                <span className="flex-1">{item.label}</span>
              </Link>
            );
          })}
        </div>

        <div className="flex-1" />

        {/* Footer: user pill */}
        <div className="border-t border-solid border-[var(--border-subtle)] pt-4 flex items-center gap-2.5">
          <Avatar name={user.name} size={36} />
          <div className="min-w-0 flex-1">
            <div
              className="text-sm font-semibold overflow-hidden text-ellipsis whitespace-nowrap"
              title={user.email}
            >
              {displayName}
            </div>
            <div className="text-xs text-[var(--fg-tertiary)] overflow-hidden text-ellipsis whitespace-nowrap">
              {user.email}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
